/**
 * Team Inbox — desktop browser notifications (R-D5 stop-gap, Phase 2).
 *
 * The Capacitor/FCM push is Phase 3; until store review clears, whoever
 * keeps the portal open gets browser Notifications for:
 *   • a new message on a chat THEY own
 *   • a chat newly ASSIGNED to them
 *   • (Admin, per the owner-locked default scope) unassigned activity +
 *     escalations — or everything when adminScope = 'all'
 *
 * Initialised INSIDE the module (TeamInbox mount) — main.tsx is untouched
 * (lane rule; the Phase-3 initPush() one-liner stays a MERGE_NOTES item).
 *
 * The DECISION half (`notificationPlan` + quiet hours + prefs) is pure and
 * test-locked; only `desktopNotifier()` touches the Notification API.
 */
import type {
  EscalationItem,
  InboxConversation,
  InboxMeta,
} from './types';

/* ───────────────────── per-user prefs (this device) ───────────────────── */

export interface NotifyPrefs {
  /** Master mute for THIS user on THIS device (S6 per-user toggle). */
  enabled: boolean;
}

const PREFS_PREFIX = 'ti.notify.';

/** PURE-ish — parse a stored prefs string. Default: enabled (stop-gap is
 *  opt-out via S6; the browser permission prompt is the real gate). */
export function parsePrefs(raw: string | null): NotifyPrefs {
  if (!raw) return { enabled: true };
  try {
    const p = JSON.parse(raw) as { enabled?: unknown };
    return { enabled: p.enabled !== false };
  } catch {
    return { enabled: true };
  }
}

export function loadPrefs(staffId: string | null): NotifyPrefs {
  try {
    return parsePrefs(
      window.localStorage.getItem(PREFS_PREFIX + (staffId || 'anon')),
    );
  } catch {
    return { enabled: true };
  }
}

export function savePrefs(staffId: string | null, prefs: NotifyPrefs): void {
  try {
    window.localStorage.setItem(
      PREFS_PREFIX + (staffId || 'anon'),
      JSON.stringify(prefs),
    );
  } catch {
    /* storage unavailable — prefs just don't persist */
  }
}

/* ───────────────────── quiet hours ───────────────────── */

/**
 * PURE — inside the configured quiet window? Times are interpreted in the
 * DEVICE's local clock (desktop stop-gap; FCM quiet hours land Phase 3).
 * Overnight ranges (22:00–08:00) wrap midnight.
 */
export function inQuietHours(
  quiet: { start: string; end: string } | null | undefined,
  nowMs: number,
): boolean {
  if (!quiet) return false;
  const m = (s: string) => {
    const mm = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(s);
    return mm ? Number(mm[1]) * 60 + Number(mm[2]) : null;
  };
  const s = m(quiet.start);
  const e = m(quiet.end);
  if (s == null || e == null) return false;
  const d = new Date(nowMs);
  const cur = d.getHours() * 60 + d.getMinutes();
  return s < e ? cur >= s && cur < e : cur >= s || cur < e;
}

/* ───────────────────── the decision (pure) ───────────────────── */

export interface NotifyItem {
  /** conversation to open on click (or null for a pure FYI). */
  key: string | null;
  title: string;
  body: string;
  /** stable tag → the browser stacks repeats per conversation (§8). */
  tag: string;
}

export interface NotifyContext {
  myIds: Array<string | null | undefined>;
  role: string;
  adminScope: 'all' | 'escalations_unassigned';
  /** escalation feed items already seen (ms watermark). */
  escalationWatermarkMs: number;
}

function mine(c: InboxConversation, myIds: NotifyContext['myIds']): boolean {
  return !!c.owner && myIds.some((id) => !!id && c.owner!.uid === id);
}

/**
 * PURE — diff two row snapshots (+ the escalation feed) into the
 * notifications to fire. §8 anatomy:
 *   assigned-to-me   → "Assigned to you · Sales" + snippet
 *   new msg, my chat → "Atul Sharma · Client Care" + snippet
 *   admin extras     → unassigned activity (always) · every message when
 *                       scope 'all' · escalation feed items
 */
export function notificationPlan(
  prev: InboxConversation[] | null,
  next: InboxConversation[],
  escalations: EscalationItem[],
  ctx: NotifyContext,
): { items: NotifyItem[]; escalationWatermarkMs: number } {
  const items: NotifyItem[] = [];
  const prevByKey = new Map((prev || []).map((c) => [c.key, c]));
  const isAdmin = ctx.role === 'admin';

  if (prev) {
    for (const c of next) {
      const before = prevByKey.get(c.key);
      const label = c.number?.label ? ` · ${c.number.label}` : '';

      // Newly assigned to me (owner flipped onto one of my ids).
      const wasMine = before ? mine(before, ctx.myIds) : false;
      if (mine(c, ctx.myIds) && !wasMine && before) {
        items.push({
          key: c.key,
          title: `Assigned to you${label}`,
          body: `${c.name}: ${c.snippet}`,
          tag: `ti-assign-${c.key}`,
        });
        continue; // one notification per row per tick is enough
      }

      // New inbound activity (unread grew).
      const grew = before ? c.unread > before.unread : false;
      if (!grew) continue;
      if (mine(c, ctx.myIds)) {
        items.push({
          key: c.key,
          title: `${c.name}${label}`,
          body: c.snippet,
          tag: `ti-msg-${c.key}`,
        });
      } else if (isAdmin && c.owner === null) {
        // Owner-locked admin default: unassigned activity always pings.
        items.push({
          key: c.key,
          title: `Unassigned${label}`,
          body: `${c.name}: ${c.snippet}`,
          tag: `ti-msg-${c.key}`,
        });
      } else if (isAdmin && ctx.adminScope === 'all') {
        items.push({
          key: c.key,
          title: `${c.name}${label}`,
          body: c.snippet,
          tag: `ti-msg-${c.key}`,
        });
      }
    }
  }

  // Escalation feed (admin only — the feed itself is admin-scoped server-side).
  let watermark = ctx.escalationWatermarkMs;
  for (const e of escalations || []) {
    if (typeof e.ms !== 'number' || e.ms <= ctx.escalationWatermarkMs) continue;
    watermark = Math.max(watermark, e.ms);
    if (isAdmin) {
      items.push({
        key: e.conversationKey,
        title: '⚠ Escalation',
        body: e.text,
        tag: `ti-esc-${e.conversationKey}`,
      });
    }
  }

  return { items, escalationWatermarkMs: watermark };
}

/* ───────────────────── the impure shell ───────────────────── */

export interface DesktopNotifier {
  /** Current permission: 'granted' | 'denied' | 'default' | 'unsupported'. */
  permission(): string;
  /** Ask the browser (must be a user gesture). */
  request(): Promise<string>;
  /** Fire a batch (silently no-ops when muted/blocked/quiet). */
  fire(
    items: NotifyItem[],
    opts: {
      muted: boolean;
      quietHours: { start: string; end: string } | null;
      onOpen: (key: string) => void;
    },
  ): void;
}

/** The browser Notification shell. Window-less environments (tests,
 *  SSR-ish tooling) get a safe no-op. */
export function desktopNotifier(): DesktopNotifier {
  const supported =
    typeof window !== 'undefined' && 'Notification' in window;
  return {
    permission() {
      return supported ? Notification.permission : 'unsupported';
    },
    async request() {
      if (!supported) return 'unsupported';
      try {
        return await Notification.requestPermission();
      } catch {
        return 'denied';
      }
    },
    fire(items, opts) {
      if (!supported) return;
      if (opts.muted) return;
      if (Notification.permission !== 'granted') return;
      if (inQuietHours(opts.quietHours, Date.now())) return;
      // Only notify when the tab is NOT visible — an on-screen inbox is
      // its own notification.
      if (typeof document !== 'undefined' && !document.hidden) return;
      for (const item of items.slice(0, 5)) {
        try {
          const n = new Notification(item.title, {
            body: item.body,
            tag: item.tag,
          });
          n.onclick = () => {
            try {
              window.focus();
            } catch {
              /* best-effort */
            }
            if (item.key) opts.onOpen(item.key);
            n.close();
          };
        } catch {
          /* a single failed notification never breaks the loop */
        }
      }
    },
  };
}

/** Admin scope from a meta payload (owner-locked default when absent). */
export function adminScopeOf(meta: InboxMeta | null): 'all' | 'escalations_unassigned' {
  const s = meta?.registry?.notifications?.adminScope;
  return s === 'all' ? 'all' : 'escalations_unassigned';
}

/** Quiet hours from a meta payload (null when unset). */
export function quietHoursOf(
  meta: InboxMeta | null,
): { start: string; end: string } | null {
  return meta?.registry?.notifications?.quietHours || null;
}
